import { Loader2, Store, Construction, AlertTriangle } from "lucide-react";

export function StoreLoadingScreen() {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center space-y-4">
      <Loader2 className="h-12 w-12 text-indigo-600 animate-spin" />
      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
        Loading store…
      </p>
    </div>
  );
}

export function StoreNotFoundScreen({ host }: { host: string }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-8 text-center">
      <Store className="h-16 w-16 text-slate-300 mb-4" />
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Store not found</h1>
      <p className="text-slate-600 max-w-md">
        We couldn't find a store at <span className="font-mono text-slate-800">{host}</span>.
        Double-check the address, or contact the store you were looking for.
      </p>
    </div>
  );
}

export function StoreUnavailableScreen() {
  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-8 text-center">
      <Store className="h-16 w-16 text-slate-600 mb-4" />
      <h1 className="text-2xl font-bold mb-2">Store unavailable</h1>
      <p className="text-slate-400 max-w-md">
        This storefront is not accepting visits right now. Please check back later
        or contact the store administrator.
      </p>
    </div>
  );
}

export function StoreMaintenanceScreen({ message }: { message?: string | null }) {
  const msg =
    (message || "").trim() ||
    "We're making improvements. The store will be back shortly.";
  return (
    <div className="min-h-screen bg-amber-950 text-amber-50 flex flex-col items-center justify-center p-8 text-center">
      <Construction className="h-16 w-16 text-amber-400 mb-4" />
      <h1 className="text-2xl font-bold mb-2">Under maintenance</h1>
      <p className="text-amber-200/90 max-w-md">{msg}</p>
    </div>
  );
}

/**
 * Generic error screen for the public storefront. We deliberately do NOT
 * surface the raw RPC / Postgres error string to anonymous visitors —
 * those messages can leak internal column names, schema details, or
 * configuration hints to an attacker probing the boundary. The message
 * is logged to the console for debugging, and the user gets a generic
 * "try again" affordance.
 */
export function StoreErrorScreen({
  message,
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  if (message) {
    console.error("[storefront] host org load failed:", message);
  }
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-8 text-center">
      <AlertTriangle className="h-16 w-16 text-rose-500 mb-4" />
      <h1 className="text-2xl font-bold text-slate-800 mb-2">Something went wrong</h1>
      <p className="text-slate-600 max-w-md">
        We couldn't load this store right now. Please try again in a moment.
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 inline-flex items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function StoreComingSoonScreen({ name }: { name: string }) {
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center">
      <Store className="h-16 w-16 text-slate-300 mb-4" />
      <h1 className="text-2xl font-bold text-slate-800 mb-2">{name}</h1>
      <p className="text-slate-600 max-w-md">
        Store coming soon. Please check back shortly.
      </p>
    </div>
  );
}
